import I18n from '@js/modules/translations';

import {RIDE_TYPE_ICON} from '@js/components/themes/icons';
import mapMarkerRadiusIcon from '@js/components/icons/mapMarkerRadiusIcon';

import {parseIsoDate, periodLabel} from '@js/modules/plan/planPeriods';

export const PLAN_SORTS = ['date', 'place', 'distance', 'duration'];

// done: completed, or only done rides; overdue: its period is over (server rule); ride: a planned ride.
export const planPoiState = (planPoi) => {
    const rides = planPoi.associatedRides || [];

    if (planPoi.planDateType === 'completed' || (rides.length > 0 && rides.every((ride) => !ride.planned))) {
        return 'done';
    }
    if (planPoi.overdue) {
        return 'overdue';
    }

    return rides.length > 0 ? 'ride' : 'todo';
};

// The same bubble on the map and in the list: activity icon in a circle, tip pointing at the place.
export const planPoiPinHtml = (planPoi) => {
    const state = planPoiState(planPoi);
    const icon = RIDE_TYPE_ICON[planPoi.rideType] || mapMarkerRadiusIcon;
    const badge = state === 'overdue' ? '<span class="plan-poi-pin-badge">!</span>' : (state === 'done' ? '<span class="plan-poi-pin-badge">✓</span>' : '');

    return `<span class="plan-poi-pin plan-poi-pin-${state}" aria-hidden="true">`
        + `<span class="plan-poi-pin-bubble"><svg viewBox="0 0 24 24" focusable="false"><path d="${icon}"/></svg>${badge}</span>`
        + '<span class="plan-poi-pin-tip"></span>'
        + '</span>';
};

export const planPoiPeriodLabel = (planPoi) => {
    if (planPoi.planDateType === 'completed') {
        return planPoi.completedAt ? periodLabel('date', planPoi.completedAt) : '';
    }

    return planPoi.planPeriodStart ? periodLabel(planPoi.planDateType, planPoi.planPeriodStart) : '';
};

export const planPoiPlace = (planPoi) => [planPoi.city, planPoi.state].filter(Boolean).join(', ');

const firstRide = (planPoi) => (planPoi.associatedRides || [])[0];

const byTitle = (a, b) => String(a.title || '').localeCompare(String(b.title || ''));

const endOf = (date, period) => {
    if (period === 'week') {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + (7 - ((date.getDay() + 6) % 7)));
    }

    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
};

// By when it is due: overdue first, then this week, this month, later; undated and done last.
const dateGroup = (planPoi, now) => {
    const state = planPoiState(planPoi);
    if (state === 'done') {
        return 'completed';
    }
    if (state === 'overdue') {
        return 'overdue';
    }
    if (!planPoi.planPeriodEnd) {
        return 'undated';
    }

    const periodEnd = parseIsoDate(planPoi.planPeriodEnd);
    if (periodEnd < endOf(now, 'week')) {
        return 'this_week';
    }

    return periodEnd < endOf(now, 'month') ? 'this_month' : 'later';
};

const DATE_GROUPS = ['overdue', 'this_week', 'this_month', 'later', 'undated', 'completed'];

const UNKNOWN_PLACE = 'unknown_place';

const compareRideMetric = (metric) => (a, b) => {
    const valueA = firstRide(a)?.[metric];
    const valueB = firstRide(b)?.[metric];

    if (valueA == null && valueB == null) {
        return byTitle(a, b);
    }
    if (valueA == null) {
        return 1;
    }
    if (valueB == null) {
        return -1;
    }

    return valueA - valueB || byTitle(a, b);
};

// Sections of the list for a sort: [{key, label, planPois}]; distance and duration are one flat section.
export const groupPlanPois = (planPois, sort = 'date', now = new Date()) => {
    if (sort === 'distance' || sort === 'duration') {
        return [{key: sort, label: null, planPois: [...planPois].sort(compareRideMetric(sort))}];
    }

    const groups = new Map();
    const add = (key, label, planPoi) => {
        if (!groups.has(key)) {
            groups.set(key, {key, label, planPois: []});
        }
        groups.get(key).planPois.push(planPoi);
    };

    if (sort === 'place') {
        planPois.forEach((planPoi) => {
            const region = [planPoi.state, planPoi.country].filter(Boolean).join(', ');
            add(region || UNKNOWN_PLACE, region || I18n.t('js.ride.plan.index.list.groups.unknown_place'), planPoi);
        });

        // Unknown place last: localeCompare puts punctuation before letters
        const sections = [...groups.values()].sort((a, b) => (a.key === UNKNOWN_PLACE) - (b.key === UNKNOWN_PLACE) || a.key.localeCompare(b.key));
        sections.forEach((section) => section.planPois.sort((a, b) => String(a.city || '').localeCompare(String(b.city || '')) || byTitle(a, b)));

        return sections;
    }

    planPois.forEach((planPoi) => {
        const key = dateGroup(planPoi, now);
        add(key, I18n.t(`js.ride.plan.index.list.groups.${key}`), planPoi);
    });

    const sections = DATE_GROUPS.filter((key) => groups.has(key)).map((key) => groups.get(key));
    sections.forEach((section) => section.planPois.sort((a, b) => {
        const endA = a.planPeriodEnd || a.completedAt || '';
        const endB = b.planPeriodEnd || b.completedAt || '';

        return section.key === 'completed' ? endB.localeCompare(endA) || byTitle(a, b) : endA.localeCompare(endB) || byTitle(a, b);
    }));

    return sections;
};
